import { b2Vec2 } from "./box2d-compat.js";
import { FlashLevelBaseRuntime } from "./flash-level-base-runtime.js";

const GOAL_SYMBOL = Object.freeze({
  imageWidth: 25.2,
  imageHeight: 46.5,
  imageOffsetX: -1.0,
  imageOffsetY: -15.2,
});

const CHECKPOINT_SYMBOL = Object.freeze({
  imageWidth: 10.8,
  imageHeight: 21.1,
  imageOffsetX: -0.5,
  imageOffsetY: -0.5,
});

const LEVEL12_DYNAMIC_SYMBOLS = Object.freeze({
  killStar: {
    imagePath: "port/generated/symbols/128/png/1.png",
    width: 41.0,
    height: 41.0,
    offsetX: -0.5,
    offsetY: -0.5,
  },
  box: {
    imagePath: "port/generated/symbols/133/png/1.png",
    width: 174.0,
    height: 97.0,
    offsetX: -7.0,
    offsetY: -58.15,
  },
  bigBox: {
    imagePath: "port/generated/symbols/149/png/1.png",
    width: 193.15,
    height: 138.75,
    offsetX: -113.6,
    offsetY: -58.05,
  },
  roll: {
    imagePath: "port/generated/symbols/157/png/1.png",
    width: 379.5,
    height: 348.9,
    offsetX: -141.45,
    offsetY: -156.9,
  },
  goPlat: {
    imagePath: "port/generated/symbols/159/png/1.png",
    width: 60.75,
    height: 16.75,
    offsetX: -0.5,
    offsetY: -0.5,
  },
});

const LEVEL12_DYNAMIC_ORDER = Object.freeze([
  "killStar",
  "box0",
  "box1",
  "box2",
  "bigBox",
  "roll1",
  "roll2",
  "goPlat",
]);

function drawSymbolImage(ctx, image, symbol, x, y, rotationDeg = 0) {
  if (!image?.complete || !symbol) {
    return;
  }
  ctx.save();
  ctx.translate(x, y);
  ctx.rotate((rotationDeg * Math.PI) / 180);
  ctx.drawImage(image, symbol.offsetX, symbol.offsetY, symbol.width, symbol.height);
  ctx.restore();
}

function drawAnimatedFrame(ctx, image, symbol, x, y, rotationDeg = 0) {
  if (!image?.complete || !symbol) {
    return;
  }
  ctx.save();
  ctx.translate(x, y);
  if (rotationDeg) {
    ctx.rotate((rotationDeg * Math.PI) / 180);
  }
  ctx.drawImage(image, symbol.imageOffsetX, symbol.imageOffsetY, symbol.imageWidth, symbol.imageHeight);
  ctx.restore();
}

export class LevelTwelveRuntime extends FlashLevelBaseRuntime {
  buildLevel() {
    if (this.assetsEnabled) {
      this.stageImage.src = "port/generated/levels_fg/level_12/1.visual.runtime.svg";
      this.level12DynamicImages = {
        killStar: new Image(),
        box: new Image(),
        bigBox: new Image(),
        roll: new Image(),
        goPlat: new Image(),
      };
      for (const [key, symbol] of Object.entries(LEVEL12_DYNAMIC_SYMBOLS)) {
        this.level12DynamicImages[key].src = symbol.imagePath;
      }
    } else {
      this.level12DynamicImages = {};
    }

    this.createAllLevelBodies();
    this.killStarBody = this.getBody("killStar");
    this.applyBodyGroupIndex(this.getBody("mainPlatform"), -1);
    this.applyBodyGroupIndex(this.killStarBody, -1);
    this.addPrismaticJoint(this.killStarBody, this.ground, new b2Vec2(this.killStarBody.GetPosition().x + 20 / 30, this.killStarBody.GetPosition().y + 20 / 30), new b2Vec2(1, 0));
    this.addDistanceJoint(this.getBody("goPlat"), this.getBody("roll1"), new b2Vec2(this.getBody("goPlat").GetPosition().x + 3 / 30, this.getBody("goPlat").GetPosition().y + 1 / 30), new b2Vec2(this.getBody("roll1").GetPosition().x + 4 / 30, this.getBody("roll1").GetPosition().y + 4 / 30));
    this.addDistanceJoint(this.getBody("goPlat"), this.getBody("roll2"), new b2Vec2(this.getBody("goPlat").GetPosition().x + 57 / 30, this.getBody("goPlat").GetPosition().y + 1 / 30), new b2Vec2(this.getBody("roll2").GetPosition().x + 4 / 30, this.getBody("roll2").GetPosition().y + 4 / 30));
    this.addDistanceJoint(this.getBody("roll1"), this.getBody("roll2"), new b2Vec2(this.getBody("roll1").GetPosition().x + 4 / 30, this.getBody("roll1").GetPosition().y + 4 / 30), new b2Vec2(this.getBody("roll2").GetPosition().x + 4 / 30, this.getBody("roll2").GetPosition().y + 4 / 30));
    this.killStarDirection = 1;
  }

  levelUpdate() {
    if (this.player.y > 500 || this.contactListener.playerContactBodies.indexOf(this.killStarBody) !== -1) {
      if (this.player.isLive()) {
        this.startDeath();
      }
    }
    const killStar = this.getDisplayLayout("killStar");
    if (killStar.x < -1050) {
      this.killStarDirection = 1;
    }
    if (killStar.x > -824) {
      this.killStarDirection = -1;
    }
    this.killStarBody.SetLinearVelocity(new b2Vec2(4 * this.killStarDirection, 0));
  }

  getLevel12SymbolForBody(name) {
    if (name === "killStar") {
      return { image: this.level12DynamicImages.killStar, symbol: LEVEL12_DYNAMIC_SYMBOLS.killStar };
    }
    if (name === "box0" || name === "box1" || name === "box2") {
      return { image: this.level12DynamicImages.box, symbol: LEVEL12_DYNAMIC_SYMBOLS.box };
    }
    if (name === "bigBox") {
      return { image: this.level12DynamicImages.bigBox, symbol: LEVEL12_DYNAMIC_SYMBOLS.bigBox };
    }
    if (name === "roll1" || name === "roll2") {
      return { image: this.level12DynamicImages.roll, symbol: LEVEL12_DYNAMIC_SYMBOLS.roll };
    }
    if (name === "goPlat") {
      return { image: this.level12DynamicImages.goPlat, symbol: LEVEL12_DYNAMIC_SYMBOLS.goPlat };
    }
    return null;
  }

  drawLevel12Bodies(ctx) {
    for (const name of LEVEL12_DYNAMIC_ORDER) {
      const layout = this.getDisplayLayout(name);
      if (!layout) {
        continue;
      }
      const mapped = this.getLevel12SymbolForBody(name);
      if (!mapped) {
        continue;
      }
      drawSymbolImage(
        ctx,
        mapped.image,
        mapped.symbol,
        layout.x,
        layout.y,
        layout.rotation ?? 0,
      );
    }
  }

  render(ctx, stageWidth, stageHeight, options = {}) {
    const zoom = Math.min(4, Math.max(0.1, Number(options.zoom) || 1));
    ctx.save();
    ctx.clearRect(0, 0, stageWidth, stageHeight);
    ctx.translate(stageWidth / 2, stageHeight / 2);
    ctx.scale(zoom, zoom);
    ctx.translate(-stageWidth / 2, -stageHeight / 2);

    if (this.backgroundImage?.complete && this.backgroundStart && this.backgroundSymbol?.layout) {
      const sym = this.backgroundSymbol;
      const bx = this.backgroundStart.x - this.camera.x / 10;
      const by = this.backgroundStart.y - this.camera.y / 10;
      ctx.save();
      ctx.translate(bx, by);
      if (sym.pngLayout) {
        const scaleX = sym.layout.imageWidth / sym.pngLayout.sourceWidth;
        const scaleY = sym.layout.imageHeight / sym.pngLayout.sourceHeight;
        ctx.drawImage(
          this.backgroundImage,
          sym.pngLayout.cropX, sym.pngLayout.cropY,
          sym.pngLayout.cropWidth, sym.pngLayout.cropHeight,
          sym.layout.imageOffsetX + sym.pngLayout.cropX * scaleX,
          sym.layout.imageOffsetY + sym.pngLayout.cropY * scaleY,
          sym.pngLayout.cropWidth * scaleX,
          sym.pngLayout.cropHeight * scaleY,
        );
      } else {
        ctx.drawImage(this.backgroundImage, sym.layout.imageOffsetX, sym.layout.imageOffsetY);
      }
      ctx.restore();
    }

    ctx.translate(this.camera.x, this.camera.y);

    if (this.stageImage?.complete) {
      const { layout } = this.levelData;
      ctx.drawImage(this.stageImage, layout.imageOffsetX, layout.imageOffsetY, layout.imageWidth, layout.imageHeight);
    }

    this.drawLevel12Bodies(ctx);

    for (const checkpointState of this.checkpoints) {
      const checkpointFrame = this.checkpointFrames[checkpointState.currentFrame - 1];
      drawAnimatedFrame(ctx, checkpointFrame, CHECKPOINT_SYMBOL, checkpointState.x, checkpointState.y);
    }

    const goalFrame = this.goalFrames[this.goalCurrentFrame - 1];
    drawAnimatedFrame(ctx, goalFrame, GOAL_SYMBOL, this.goalPosition.x, this.goalPosition.y);

    if (this.player?.isLive?.()) {
      ctx.save();
      ctx.translate(this.player.x, this.player.y);
      ctx.rotate(this.player.body.GetAngle());
      if (this.playerImage?.complete) {
        ctx.drawImage(this.playerImage, -10.5, -10.5);
      }
      ctx.restore();
    }

    ctx.fillStyle = "#d32f2f";
    for (const particle of this.particles) {
      ctx.beginPath();
      ctx.arc(particle.x, particle.y, 4, 0, Math.PI * 2);
      ctx.fill();
    }

    ctx.restore();
  }
}
